export class SimpleCache {
  constructor() {
    this.cache = new Map()
  }

  async invoke(func, options = {}) {
    const { key: cacheKey, maxAgeInSeconds } = options
    if (!cacheKey) {
      throw new Error('missing cache key')
    }

    // Check if we have a cached entry
    if (this.cache.has(cacheKey)) {
      const entry = this.cache.get(cacheKey)
      const now = Date.now()
      const isExpired = maxAgeInSeconds !== undefined &&
          now > (entry.timestamp + maxAgeInSeconds * 1000)

      if (!isExpired) {
        return entry.data
      }
    }

    // Execute function and cache the promise
    const promise = func()
    const entry = { data: promise, timestamp: Date.now() }
    this.cache.set(cacheKey, entry)

    try {
      return await promise
    } catch (err) {
      // Remove failed promise from cache
      this.cache.delete(cacheKey)
      throw err
    }
  }

  // Read a live entry WITHOUT invoking the producer — returns the cached promise,
  // or undefined when the key is absent or past maxAgeInSeconds. Callers that only
  // want to inspect what a subsequent `invoke` would hand back (e.g. to decide
  // whether the entry is now stale and must be purged) use this so the inspection
  // itself can never trigger a fetch. The stored value is a promise that may
  // reject, so await it defensively.
  peek(cacheKey, maxAgeInSeconds) {
    if (!this.cache.has(cacheKey)) {
      return undefined
    }
    const entry = this.cache.get(cacheKey)
    const isExpired = maxAgeInSeconds !== undefined &&
        Date.now() > (entry.timestamp + maxAgeInSeconds * 1000)

    return isExpired ? undefined : entry.data
  }

  purge(cacheKey) {
    this.cache.delete(cacheKey)
  }
}
